import * as Sentry from "@sentry/react";
import { UseQueryResult, useQuery } from "react-query";

import axios from "axios";

import { DATA_SVC_RS_ADDRESS } from "../../../constants/app.constants";
import { FieldResult, StickDataRequest, StickRow } from "../models/stickData";
import { convertStickDataToRows } from "../utils/convertStickData";

const API_URL = `${DATA_SVC_RS_ADDRESS}/api/v1/data/stick`;

export const StickDataQueryKey = "StickData";

/**
 * Fetches stick data from data-svc-rs and converts columnar format to row-based
 *
 * @param request - StickDataRequest with fields, scenarioIds, and optional settings
 * @returns Promise resolving to array of StickRow objects
 */
const fetchStickData = async (
  request: StickDataRequest,
  stickIds: string[]
): Promise<StickRow[]> => {
  return Sentry.startSpan(
    {
      op: "http.client",
      name: "POST /stick"
    },
    async (span) => {
      span.setAttribute("scenarioCount", request.scenarioIds.length);
      span.setAttribute("fieldCount", request.fields.length);

      try {
        const response = await axios.post<FieldResult[]>(API_URL, {
          fields: request.fields,
          pdenSource: request.pdenSource,
          normalizeBySetting: request.normalizeBySetting,
          precision: request.precision,
          scenarioIds: request.scenarioIds
        });

        const { logger } = Sentry;
        logger.debug(
          logger.fmt`Fetched stick data for ${request.scenarioIds.length} scenarios with ${request.fields.length} fields`
        );

        // Convert columnar data to row-based format, using provided stick IDs if available
        const rows = convertStickDataToRows(response.data || [], stickIds);
        span.setAttribute("rowCount", rows.length);
        return rows;
      } catch (error) {
        Sentry.captureException(error);
        const { logger } = Sentry;
        logger.error("Failed to fetch stick data", {
          scenarioIds: request.scenarioIds,
          error: error instanceof Error ? error.message : String(error)
        });
        throw error;
      }
    }
  );
};

const useStickData = (
  request: StickDataRequest,
  enabled: boolean = true,
  stickIds?: string[]
): UseQueryResult<StickRow[], Error> => {
  return useQuery<StickRow[], Error>(
    [StickDataQueryKey, request],
    () => fetchStickData(request, stickIds),
    {
      enabled: enabled && request.scenarioIds.length > 0,
      refetchOnWindowFocus: false,
      retry: 2,
      onError: (error) => {
        const { logger } = Sentry;
        logger.error("useStickData query failed", {
          scenarioIds: request.scenarioIds,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
  );
};

export default useStickData;
